import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CreateMultipleDefaultQuoteItemsPayload, DefaultQuoteItemModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/defaultQuoteItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/defaultQuoteItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/defaultQuoteItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/defaultQuoteItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/defaultQuoteItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/defaultQuoteItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/defaultQuoteItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/defaultQuoteItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/defaultQuoteItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/defaultQuoteItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/defaultQuoteItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/defaultQuoteItem/DELETE_FAILURE'

export type DefaultQuoteItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: DefaultQuoteItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadDefaultQuoteItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().defaultQuoteItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/default_quote_items.json', options)
      .then(({ data }: { data: DefaultQuoteItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.DEFAULT_QUOTE_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadDefaultQuoteItem(defaultQuoteItemId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.defaultQuoteItems.loadedForKeys]
    const loadedIds = [...state.defaultQuoteItems.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (defaultQuoteItemId && !loadedIds.includes(defaultQuoteItemId)){
      loadedIds.push(defaultQuoteItemId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/default_quote_items/${defaultQuoteItemId}.json`, options)
      .then(({ data }: { data: DefaultQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.DEFAULT_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createDefaultQuoteItem(defaultQuoteItem: Partial<DefaultQuoteItemModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      default_quote_item: defaultQuoteItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/default_quote_items.json', options, config)
      .then(({ data }: { data: DefaultQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.DEFAULT_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createDefaultQuoteItems(itemPayload: CreateMultipleDefaultQuoteItemsPayload, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      default_quote_item: itemPayload,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/default_quote_items/create_multiple.json', options, config)
      .then(({ data }: { data: DefaultQuoteItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.DEFAULT_QUOTE_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateDefaultQuoteItem(
  defaultQuoteItem: UpdateParams<DefaultQuoteItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      default_quote_item: defaultQuoteItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/default_quote_items/${defaultQuoteItem.id}.json`, options, config)
      .then(({ data }: { data: DefaultQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.DEFAULT_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteDefaultQuoteItem(defaultQuoteItem: DeleteParams<DefaultQuoteItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/default_quote_items/${defaultQuoteItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.DEFAULT_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: DefaultQuoteItemModuleState = initialState,
  action: Action = { type: '' },
): DefaultQuoteItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
